import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { PageHead, Badge } from "../../components/ui-bits";
import { toast } from "sonner";
import { EnvelopeSimple, Phone } from "@phosphor-icons/react";

const STATUSES = ["new", "contacted", "converted"];
const KEY = { new: "overdue", contacted: "upcoming", converted: "completed" };

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (l, status) => {
    await api.patch(`/admin/leads/${l.id}`, { status });
    toast.success(`Lead marked ${status}`);
    setLeads((prev) => prev.map((x) => (x.id === l.id ? { ...x, status } : x)));
  };

  const convert = async (l) => {
    try {
      const { data } = await api.post(`/admin/leads/${l.id}/convert`);
      toast.success(`Client created · temp password: ${data.temp_password}${data.agent ? ` · CFO: ${data.agent}` : ""}`, { duration: 10000 });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not convert lead"); }
  };

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: leads.filter((l) => l.status === s).length }), {});
  const filtered = useMemo(() => leads.filter((l) => filter === "all" || l.status === filter), [leads, filter]);

  const analytics = useMemo(() => {
    const bySource = {};
    leads.forEach((l) => {
      const s = l.source || "Consultation Form";
      const r = bySource[s] || (bySource[s] = { source: s, total: 0, converted: 0 });
      r.total += 1;
      if (l.status === "converted") r.converted += 1;
    });
    const list = Object.values(bySource).sort((a, b) => b.total - a.total);
    const max = Math.max(1, ...list.map((r) => r.total));
    const conv = leads.length ? Math.round((leads.filter((l) => l.status === "converted").length / leads.length) * 100) : 0;
    return { list, max, conv };
  }, [leads]);

  return (
    <div>
      <PageHead eyebrow="Website Enquiries" title="Leads Inbox" />
      <div className="grid grid-cols-3 gap-4 mb-6">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`lead-count-${s}`} onClick={() => setFilter(filter === s ? "all" : s)}
            className={`card p-5 text-left transition-all hover:-translate-y-0.5 ${filter === s ? "ring-2 ring-[#002FA7]" : ""}`}>
            <p className="label mb-1 capitalize">{s}</p>
            <p className="font-head font-black text-3xl tnum">{counts[s]}</p>
          </button>
        ))}
      </div>
      {leads.length > 0 && (
        <div data-testid="lead-analytics" className="card p-6 mb-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <p className="label mb-3">Leads by Source</p>
            <div className="space-y-2.5">
              {analytics.list.map((r) => (
                <div key={r.source} data-testid={`lead-src-${r.source.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-3 text-sm">
                  <span className="w-40 shrink-0 truncate">{r.source}</span>
                  <div className="flex-1 h-5 bg-black/5 rounded-sm overflow-hidden">
                    <div className="h-full bg-[#002FA7] flex items-center justify-end px-2 text-white text-[11px]" style={{ width: `${(r.total / analytics.max) * 100}%` }}>{r.total}</div>
                  </div>
                  <span className="text-xs text-[#888] w-24 text-right">{r.converted} converted</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-center justify-center border-l border-black/10 pl-6">
            <p className="label mb-1">Conversion Rate</p>
            <p data-testid="lead-conv-rate" className="font-head font-black text-5xl tnum text-[#00A86B]">{analytics.conv}%</p>
            <p className="text-xs text-[#888] mt-1">{leads.filter((l) => l.status === "converted").length} of {leads.length} leads</p>
          </div>
        </div>
      )}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="label text-left border-b border-black/10">
            <th className="px-5 py-3">Name</th><th className="py-3">Contact</th><th className="py-3">Plan</th><th className="py-3">Source</th>
            <th className="py-3">Message</th><th className="py-3">Status</th><th className="py-3 text-right pr-5">Actions</th></tr></thead>
          <tbody>
            {filtered.map((l, i) => (
              <tr key={l.id} data-testid={`lead-row-${i}`} className="border-b border-black/5 align-top">
                <td className="px-5 py-3 font-medium">{l.name}<br /><span className="text-xs text-[#888]">{(l.created_at || "").slice(0, 10)}</span></td>
                <td className="py-3 text-[#555]">
                  <span className="flex items-center gap-1 text-xs"><EnvelopeSimple size={13} /> {l.email}</span>
                  {l.phone && <span className="flex items-center gap-1 text-xs mt-0.5"><Phone size={13} /> {l.phone}</span>}
                </td>
                <td className="py-3">{l.plan || "—"}</td>
                <td className="py-3"><span className="text-xs bg-black/5 px-2 py-1 rounded-sm">{l.source || "Consultation Form"}</span></td>
                <td className="py-3 text-[#555] max-w-[280px]">{l.message || "—"}</td>
                <td className="py-3"><Badge status={KEY[l.status] || "upcoming"}>{l.status}</Badge></td>
                <td className="py-3 text-right pr-5">
                  <div className="flex items-center gap-2 justify-end">
                    <select data-testid={`lead-status-${i}`} value={l.status} onChange={(e) => setStatus(l, e.target.value)}
                      className="border border-black/20 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                      {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                    </select>
                    {l.status !== "converted" && (
                      <button data-testid={`lead-convert-${i}`} onClick={() => convert(l)}
                        className="text-xs bg-[#00A86B] hover:brightness-95 text-white px-3 py-1.5 rounded-sm whitespace-nowrap transition-all">
                        Convert to Client
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-[#888]">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
